import { useState } from 'react';
import { useTranslation } from '../../i18n';

const ArrowSvg = ({ color = '#0F6E56' }: { color?: string }) => (
  <svg className="chip-arr" width="22" height="12" viewBox="0 0 22 12" fill="none">
    <line x1="0" y1="6" x2="15" y2="6" stroke={color} strokeWidth="1.8" />
    <path d="M13 2L19 6L13 10" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" fill="none" />
  </svg>
);

const MAIN_SKILLS = [
  'anthropics', 'microsoft', 'github', 'vercel', 'cloudflare', 'getsentry',
  'firebase', 'stripe', 'supabase', 'openai', 'hashicorp', 'langchain-ai',
  'prisma', 'posthog', 'flutter', 'shopify', 'figma', 'auth0',
];
const EXTRA_SKILLS = [
  'datadog-labs', 'huggingface', 'firecrawl', 'pulumi', 'redis', 'clerk',
  'expo', 'tavily-ai', 'n8n-io', 'makenotion', 'bitwarden', 'semgrep',
  'dbt-labs', 'facebook', 'neondatabase', 'streamlit', 'dagster-io', 'wix',
  'upstash', 'sanity-io', 'brave', 'apify', 'wordpress', 'vercel-labs',
];
const SKILL_NAMES: Record<string, string> = {
  'anthropics': 'anthropic', 'langchain-ai': 'langchain', 'getsentry': 'sentry',
  'datadog-labs': 'datadog', 'tavily-ai': 'tavily', 'n8n-io': 'n8n',
  'makenotion': 'notion', 'dbt-labs': 'dbt-labs', 'facebook': 'react',
  'neondatabase': 'neon', 'dagster-io': 'dagster', 'sanity-io': 'sanity',
  'vercel-labs': 'vercel-labs',
};

function SkillItem({ gh }: { gh: string }) {
  const name = SKILL_NAMES[gh] ?? gh;
  return (
    <div className="skill-item">
      <img
        src={`https://github.com/${gh}.png?size=48`}
        alt={name}
        onError={e => { (e.currentTarget as HTMLImageElement).style.background = '#eee'; }}
      />
      <div className="skill-name">{name}</div>
    </div>
  );
}

const PKG_TAGS: Record<number, string[]> = {
  0: ['PCI-DSS', 'Payment', 'Audit Log'],
  1: ['COBOL→Java', 'Vue', 'Refactor'],
  2: ['Prometheus', 'Beyla', 'onTune'],
  3: ['Compliance', 'Security', 'Audit'],
};

export default function LayerB() {
  const { dict } = useTranslation();
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="layer layer-b">
      <div className="layer-wm-wrap">
        <img
          className="layer-wm"
          src="https://ntq.com.vn/wp-content/themes/ntq_branding/assets/images/NTQ-logo.png"
          alt=""
          aria-hidden="true"
          onError={e => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
        />
      </div>

      <div className="layer-header">
        <span className="layer-badge badge-b">{dict.lb_badge}</span>
        <div>
          <div className="layer-title">AgentSkills Hub</div>
          <div className="layer-sub">{dict.lb_sub}</div>
        </div>
      </div>

      <div className="layer-body">
        <div className="sub2">
          {/* Official Skills */}
          <div className="sub-card" style={{ padding: '13px' }}>
            <div className="sub-card-t"><span className="dot db" /><span>{dict.lb_official_t}</span></div>
            <div className="sub-card-d mt4">{dict.lb_official_d}</div>
            <div className="skills-grid mt8">
              {MAIN_SKILLS.map(gh => <SkillItem key={gh} gh={gh} />)}
            </div>
            {expanded && (
              <div className="skills-grid" style={{ marginTop: '5px' }}>
                {EXTRA_SKILLS.map(gh => <SkillItem key={gh} gh={gh} />)}
              </div>
            )}
            <button className="skills-toggle" onClick={() => setExpanded(v => !v)}>
              <svg
                width="10" height="7" viewBox="0 0 10 7" fill="none"
                style={{ transform: expanded ? 'rotate(180deg)' : undefined, transition: 'transform 0.2s' }}
              >
                <path d="M1 1L5 5L9 1" stroke="#0F6E56" strokeWidth="1.8" strokeLinecap="round" />
              </svg>
              <span>{expanded ? dict.lb_see_less : dict.lb_see_more}</span>
            </button>
          </div>

          {/* NTQ Custom + Packages */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <div className="sub-card" style={{ padding: '13px' }}>
              <div className="sub-card-t"><span className="dot da" /><span>{dict.lb_ntq_t}</span></div>
              <div className="sub-card-d mt4">{dict.lb_ntq_d}</div>

              <div className="ntq-cat mt8">
                <div className="ntq-cat-label">
                  <span className="ntq-cat-dot" style={{ background: '#185FA5' }} />
                  <span>{dict.ntq_cat_tech}</span>
                </div>
                <div className="ntq-skills">
                  {dict.ntq_tags_tech.map((tag, i) => (
                    <span key={i} className="ntq-tag">
                      <span className="ntq-tag-t">{tag.t}</span>
                      <span className="ntq-tag-s">{tag.s}</span>
                    </span>
                  ))}
                </div>
              </div>

              <div className="ntq-cat mt6">
                <div className="ntq-cat-label">
                  <span className="ntq-cat-dot" style={{ background: '#BA7517' }} />
                  <span>{dict.ntq_cat_domain}</span>
                </div>
                <div className="ntq-skills">
                  {dict.ntq_tags_domain.map((tag, i) => (
                    <span key={i} className="ntq-tag">
                      <span className="ntq-tag-t">{tag.t}</span>
                      <span className="ntq-tag-s">{tag.s}</span>
                    </span>
                  ))}
                </div>
              </div>

              <div className="ntq-cat mt6">
                <div className="ntq-cat-label">
                  <span className="ntq-cat-dot" style={{ background: '#0F6E56' }} />
                  <span>{dict.ntq_cat_role}</span>
                </div>
                <div className="ntq-skills">
                  {dict.ntq_tags_role.map((tag, i) => (
                    <span key={i} className="ntq-tag">
                      <span className="ntq-tag-t">{tag.t}</span>
                      <span className="ntq-tag-s">{tag.s}</span>
                    </span>
                  ))}
                </div>
              </div>
            </div>

            <div className="sub-card" style={{ padding: '13px' }}>
              <div className="sub-card-t"><span className="dot da" /><span>{dict.lb_pkg_t}</span></div>
              <div className="pkg-grid mt8">
                {[dict.pkg1_t, dict.pkg2_t, dict.pkg3_t, dict.pkg4_t].map((title, i) => (
                  <div key={i} className={`pkg-card pkg-${['a','b','c','d'][i]}`}>
                    <div className="pkg-title">{title}</div>
                    <div className="pkg-tags">
                      {PKG_TAGS[i].map(tag => <span key={tag} className="pkg-tag">{tag}</span>)}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Governance */}
        <div className="sec-label mt16">{dict.lb_gov_label}</div>
        <div className="gov-pipe">
          {dict.lb_gov_steps.map((step, i) => (
            <span key={i} style={{ display: 'contents' }}>
              <span className="gov-step">{step}</span>
              {i < dict.lb_gov_steps.length - 1 && (
                <svg width="22" height="12" viewBox="0 0 22 12" fill="none" style={{ flexShrink: 0 }}>
                  <line x1="0" y1="6" x2="15" y2="6" stroke="#554E47" strokeWidth="1.8" />
                  <path d="M13 2L19 6L13 10" stroke="#554E47" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" fill="none" />
                </svg>
              )}
            </span>
          ))}
        </div>

        {/* Flow B */}
        <div className="sec-label mt16">{dict.lb_flow_label}</div>
        <div className="flow-row">
          <span className="flow-chip fcb">{dict.lb_f1}</span>
          <ArrowSvg />
          <span className="flow-chip fcb">{dict.lb_f2}</span>
          <ArrowSvg />
          <span className="flow-chip fcb">{dict.lb_f3}</span>
        </div>
        <div className="flow-row mt6">
          <span className="flow-chip fcb">{dict.lb_f4}</span>
          <ArrowSvg />
          <span className="flow-chip fcb">{dict.lb_f5}</span>
          <ArrowSvg />
          <span className="flow-chip fcb">{dict.lb_f6}</span>
        </div>

        {/* Personas */}
        <div className="sec-label mt16">{dict.lb_persona_label}</div>
        <div className="persona-row">
          <span className="ptag"><span className="ptag-dot" style={{ background: 'var(--col-b)' }} /><span>{dict.lb_p1}</span></span>
          <span className="ptag"><span className="ptag-dot" style={{ background: 'var(--col-a)' }} /><span>{dict.lb_p2}</span></span>
          <span className="ptag"><span className="ptag-dot" style={{ background: 'var(--col-c)' }} /><span>{dict.lb_p3}</span></span>
          <span className="ptag"><span className="ptag-dot" style={{ background: '#185FA5' }} /><span>{dict.lb_p4}</span></span>
        </div>

        {/* User Flow */}
        <div className="sec-label mt16">{dict.lb_uflow_label}</div>
        <div className="hub-uflow">
          {dict.lb_uflow_steps.map((step, i) => (
            <span key={i} className="hub-uflow-item">
              <span className="hub-uflow-num">{i + 1}</span>
              <span className="hub-uflow-txt">{step}</span>
              {i < dict.lb_uflow_steps.length - 1 && <ArrowSvg color="#0F6E56" />}
            </span>
          ))}
        </div>

        {/* UI Mockup — Skills marketplace */}
        <div className="sec-label mt16">{dict.lb_ui_demo_label}</div>
        <div className="hub-mock">
          <div className="hub-mock-bar">
            <div className="hub-mock-search">
              <svg width="13" height="13" viewBox="0 0 13 13" fill="none">
                <circle cx="5.5" cy="5.5" r="4" stroke="currentColor" strokeWidth="1.4" fill="none"/>
                <line x1="8.5" y1="8.5" x2="11.5" y2="11.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
              </svg>
              <span>{dict.lb_ui_search_ph}</span>
            </div>
            <div className="hub-mock-filters">
              <span className="hub-mock-filter-lbl">{dict.lb_ui_filter_label}:</span>
              {dict.lb_ui_filter_chips.map((chip, i) => (
                <span key={i} className={`hub-mock-chip${i === 0 ? ' is-active' : ''}`}>{chip}</span>
              ))}
            </div>
          </div>
          <div className="hub-mock-grid">
            {dict.lb_ui_cards.map((card, i) => (
              <div key={i} className={`hub-mock-card${i === 0 ? ' is-selected' : ''}`}>
                <div className="hub-mock-card-head">
                  <div className="hub-mock-card-n">{card.name}</div>
                  <span className="hub-mock-card-tag">{card.tag}</span>
                </div>
                <div className="hub-mock-card-d">{card.desc}</div>
                <div className="hub-mock-card-foot">
                  <span className="hub-mock-card-dl">↓ {card.downloads}</span>
                  <button className="hub-mock-install">{dict.lb_ui_install_t}</button>
                </div>
              </div>
            ))}
          </div>
          <div className="hub-mock-cli">
            <div className="hub-mock-cli-t">💻 {dict.lb_ui_install_hint}</div>
            <div className="hub-mock-cli-cmd">
              <span className="hub-mock-cli-prompt">$</span>
              <code>./skills.sh install {dict.lb_ui_cards[0].name}</code>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
